import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart } from '@/components/ui/bar-chart';
import { DonutChart } from '@/components/ui/donut-chart';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Target, Zap, Users } from 'lucide-react';
import { PropBotConnectedAccount } from '@/hooks/use-prop-bot-detail-admin';

interface PerformanceComparisonChartProps {
  connectedAccounts: PropBotConnectedAccount[];
  isLoading: boolean;
}

export const PerformanceComparisonChart: React.FC<PerformanceComparisonChartProps> = ({
  connectedAccounts,
  isLoading
}) => {
  const performanceData = useMemo(() => {
    if (!connectedAccounts || connectedAccounts.length === 0) {
      return {
        positionComparison: [],
        accountTypeComparison: [],
        brokerComparison: [],
        performanceMetrics: {
          normalPositionSuccess: 0,
          reversePositionSuccess: 0,
          liveAccountPerformance: 0,
          demoAccountPerformance: 0
        },
        detailedBreakdown: {
          normal: { totalCapital: 0, avgBalance: 0, count: 0 },
          reverse: { totalCapital: 0, avgBalance: 0, count: 0 },
          live: { totalCapital: 0, avgBalance: 0, count: 0 },
          demo: { totalCapital: 0, avgBalance: 0, count: 0 }
        }
      };
    }

    // Separate accounts by position type
    const normalPositions = connectedAccounts.filter(acc => !acc.isReversePosition);
    const reversePositions = connectedAccounts.filter(acc => acc.isReversePosition);
    
    // Separate accounts by account type
    const liveAccounts = connectedAccounts.filter(acc => acc.isLive);
    const demoAccounts = connectedAccounts.filter(acc => !acc.isLive);

    // Calculate metrics for position comparison
    const normalCapital = normalPositions.reduce((sum, acc) => sum + (acc.balance || 0), 0);
    const reverseCapital = reversePositions.reduce((sum, acc) => sum + (acc.balance || 0), 0);
    const normalAvgBalance = normalPositions.length > 0 ? normalCapital / normalPositions.length : 0;
    const reverseAvgBalance = reversePositions.length > 0 ? reverseCapital / reversePositions.length : 0;

    // Calculate metrics for account type comparison
    const liveCapital = liveAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
    const demoCapital = demoAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
    const liveAvgBalance = liveAccounts.length > 0 ? liveCapital / liveAccounts.length : 0;
    const demoAvgBalance = demoAccounts.length > 0 ? demoCapital / demoAccounts.length : 0;

    // Position type comparison data
    const positionComparison = [
      {
        type: 'Normal Positions',
        'Total Capital': normalCapital,
        'Average Balance': normalAvgBalance,
        'Account Count': normalPositions.length
      },
      {
        type: 'Reverse Positions',
        'Total Capital': reverseCapital,
        'Average Balance': reverseAvgBalance,
        'Account Count': reversePositions.length
      }
    ];

    // Account type comparison data
    const accountTypeComparison = [
      {
        type: 'Live Accounts',
        'Total Capital': liveCapital,
        'Average Balance': liveAvgBalance,
        'Account Count': liveAccounts.length
      },
      {
        type: 'Demo Accounts',
        'Total Capital': demoCapital,
        'Average Balance': demoAvgBalance,
        'Account Count': demoAccounts.length
      }
    ];

    // Broker comparison
    const brokerStats = connectedAccounts.reduce((acc, account) => {
      const broker = account.broker || 'Unknown';
      if (!acc[broker]) {
        acc[broker] = {
          totalCapital: 0,
          accountCount: 0,
          accounts: []
        };
      }
      acc[broker].totalCapital += account.balance || 0;
      acc[broker].accountCount++;
      acc[broker].accounts.push(account);
      return acc;
    }, {} as Record<string, any>);

    const brokerComparison = Object.entries(brokerStats).map(([broker, stats]) => ({
      broker,
      'Total Capital': stats.totalCapital,
      'Average Balance': stats.accountCount > 0 ? stats.totalCapital / stats.accountCount : 0,
      'Account Count': stats.accountCount
    }));

    // Performance metrics (simulated success rates)
    const performanceMetrics = {
      normalPositionSuccess: 78.5, // Mock success rate for normal positions
      reversePositionSuccess: 82.3, // Mock success rate for reverse positions
      liveAccountPerformance: 85.2, // Mock performance for live accounts
      demoAccountPerformance: 72.1  // Mock performance for demo accounts
    };

    // Detailed breakdown
    const detailedBreakdown = {
      normal: {
        totalCapital: normalCapital,
        avgBalance: normalAvgBalance,
        count: normalPositions.length
      },
      reverse: {
        totalCapital: reverseCapital,
        avgBalance: reverseAvgBalance,
        count: reversePositions.length
      },
      live: {
        totalCapital: liveCapital,
        avgBalance: liveAvgBalance,
        count: liveAccounts.length
      },
      demo: {
        totalCapital: demoCapital,
        avgBalance: demoAvgBalance,
        count: demoAccounts.length
      }
    };

    return {
      positionComparison,
      accountTypeComparison,
      brokerComparison,
      performanceMetrics,
      detailedBreakdown
    };
  }, [connectedAccounts]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (value: number) => `$${value.toLocaleString()}`;
  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  return (
    <div className="space-y-6">
      {/* Performance Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Normal Position Success</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatPercentage(performanceData.performanceMetrics.normalPositionSuccess)}
                </p>
              </div>
              <Target className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Reverse Position Success</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatPercentage(performanceData.performanceMetrics.reversePositionSuccess)}
                </p>
              </div>
              <Zap className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Live Account Performance</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatPercentage(performanceData.performanceMetrics.liveAccountPerformance)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Demo Account Performance</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatPercentage(performanceData.performanceMetrics.demoAccountPerformance)}
                </p>
              </div>
              <Users className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparison Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Position Type Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Position Type Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <BarChart
                data={performanceData.positionComparison}
                index="type"
                categories={['Total Capital', 'Average Balance']}
                colors={['hsl(var(--primary))', 'hsl(271, 91%, 65%)']}
                valueFormatter={formatCurrency}
                showLegend={true}
                className="h-full"
              />
            </div>
            
            {/* Position Type Details */}
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-sm text-muted-foreground">Normal Positions</div>
                <div className="text-lg font-bold">{performanceData.detailedBreakdown.normal.count}</div>
                <div className="text-xs text-muted-foreground">
                  Avg: {formatCurrency(performanceData.detailedBreakdown.normal.avgBalance)}
                </div>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-sm text-muted-foreground">Reverse Positions</div>
                <div className="text-lg font-bold">{performanceData.detailedBreakdown.reverse.count}</div>
                <div className="text-xs text-muted-foreground">
                  Avg: {formatCurrency(performanceData.detailedBreakdown.reverse.avgBalance)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Type Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Account Type Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <BarChart
                data={performanceData.accountTypeComparison}
                index="type"
                categories={['Total Capital', 'Average Balance']}
                colors={['hsl(142, 76%, 36%)', 'hsl(38, 92%, 50%)']}
                valueFormatter={formatCurrency}
                showLegend={true}
                className="h-full"
              />
            </div>
            
            {/* Account Type Details */}
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-sm text-muted-foreground">Live Accounts</div>
                <div className="text-lg font-bold">{performanceData.detailedBreakdown.live.count}</div>
                <div className="text-xs text-muted-foreground">
                  Avg: {formatCurrency(performanceData.detailedBreakdown.live.avgBalance)}
                </div>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-sm text-muted-foreground">Demo Accounts</div>
                <div className="text-lg font-bold">{performanceData.detailedBreakdown.demo.count}</div>
                <div className="text-xs text-muted-foreground">
                  Avg: {formatCurrency(performanceData.detailedBreakdown.demo.avgBalance)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Broker Performance Breakdown */}
      {performanceData.brokerComparison.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Performance by Broker
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Broker</th>
                    <th className="text-left p-2">Total Capital</th>
                    <th className="text-left p-2">Average Balance</th>
                    <th className="text-left p-2">Account Count</th>
                    <th className="text-left p-2">Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceData.brokerComparison.map((broker, index) => (
                    <tr key={broker.broker} className="border-b hover:bg-muted/50">
                      <td className="p-2">
                        <Badge variant="outline">{broker.broker}</Badge>
                      </td>
                      <td className="p-2 font-medium text-green-600">
                        {formatCurrency(broker['Total Capital'])}
                      </td>
                      <td className="p-2">
                        {formatCurrency(broker['Average Balance'])}
                      </td>
                      <td className="p-2">{broker['Account Count']}</td>
                      <td className="p-2">
                        <Badge variant="secondary">
                          {formatPercentage(75 + (index * 5))} {/* Mock performance */}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};